import React from "react"
import { useTranslation } from "react-i18next"

import type { Organization } from "@/models/organization"
import { useBillLineItems } from "@/services/bill/use-bill-line-items"
import type { ChargeLineResponse } from "@/services/core-api/types"
import { useFlags } from "@/services/feature"

import { Stack, Typography } from "@mui/material"

import {
  BillChargeGroupTable,
  buildBillChargeColumns,
} from "./bill-charge-group"
import { groupBillChargeLines } from "./group-bill-charge-lines"

interface BillLineItemsDisplayProps {
  billId: number
  organizationId: Organization["id"]
}

interface BillChargesBodyProps {
  isError: boolean
  isSettled: boolean
  lines: ChargeLineResponse[] | undefined
}

const BillChargesBody: React.FC<BillChargesBodyProps> = ({
  isError,
  isSettled,
  lines,
}) => {
  const { t } = useTranslation()

  if (isError && lines == null) {
    return (
      <Typography color="error.main" role="status" variant="body2">
        {t("billCharges.unavailable")}
      </Typography>
    )
  }

  if (!isSettled || lines == null) {
    return null
  }

  const groups = groupBillChargeLines(lines)

  if (groups.length === 0) {
    return (
      <Typography color="text.secondary" variant="body2">
        {t("billCharges.empty")}
      </Typography>
    )
  }

  const columns = buildBillChargeColumns(groups.flatMap((group) => group.lines))

  return (
    <Stack sx={{ gap: 3 }}>
      {groups.map((group) => (
        <BillChargeGroupTable
          columns={columns}
          group={group}
          key={group.isMiscellaneous ? "miscellaneous" : group.title}
        />
      ))}
    </Stack>
  )
}

/** The charge breakdown for lines the caller already loaded, behind the `bill_charge_breakdown` flag. */
export const BillChargesSection: React.FC<BillChargesBodyProps> = ({
  isError,
  isSettled,
  lines,
}) => {
  const { t } = useTranslation()
  const { bill_charge_breakdown: isBillChargeBreakdownEnabled } = useFlags()

  if (!isBillChargeBreakdownEnabled) {
    return null
  }

  return (
    <Stack sx={{ gap: 2 }}>
      <Typography variant="h3">{t("billCharges.title")}</Typography>
      <BillChargesBody isError={isError} isSettled={isSettled} lines={lines} />
    </Stack>
  )
}

export const BillLineItemsDisplay: React.FC<BillLineItemsDisplayProps> = ({
  billId,
  organizationId,
}) => {
  const { bill_charge_breakdown: isBillChargeBreakdownEnabled } = useFlags()
  const { billLineItemsData, billLineItemsIsError, billLineItemsIsFetched } =
    useBillLineItems({
      billId,
      organizationId,
      queryOptions: {
        enabled: Boolean(isBillChargeBreakdownEnabled),
      },
    })

  return (
    <BillChargesSection
      isError={billLineItemsIsError}
      isSettled={billLineItemsIsFetched}
      lines={billLineItemsData}
    />
  )
}
